/**
 * 🍳📊 Kitchen POV Hub — Multi-POV Informational Meal Browser (Real World)
 *
 * Distinct from Dave's crafting kitchen (/kitchen) which is Chits-powered
 * gameplay. This page is INFORMATIONAL — no game mechanics, no combat,
 * no crafting. It presents real-world meals through three character lenses:
 *
 *   🍳 Recipes (David Lauer)         → cooking steps
 *   💰 Budget  (Cassandra & Ledger)  → cost breakdown + savings
 *   💪 Macros  (Roc)                 → calories + cut/bulk portioning
 *
 * Canon: /app/memory/DAVIDS_KITCHEN_MULTI_POV_CANON_FEB2026.md
 *
 * Backed by:
 *   GET /api/kitchen-hub/meals?pov=…
 *   GET /api/kitchen-hub/meals/:id?pov=…
 *   GET /api/kitchen-hub/chief-cook
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CassandraInsightsPanel from "@/components/CassandraInsightsPanel";
import DailyLogPanel from "@/components/DailyLogPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POV_META = {
  recipe: {
    label: "Recipes",
    host: "David Lauer",
    icon: "🍳",
    tagline: "Step-by-step cooking + technique.",
    accent: "blue",
    ring: "ring-blue-500/60",
    chip: "bg-blue-950/60 text-blue-200 border-blue-600/40",
    grad: "from-blue-600 to-indigo-700",
    text: "text-blue-300",
  },
  budget: {
    label: "Budget",
    host: "Cassandra Gutkowski & Ledger 🐦📓",
    icon: "💰",
    tagline: "Cost per serving · cheaper subs · leftover math.",
    accent: "amber",
    ring: "ring-amber-500/60",
    chip: "bg-amber-950/60 text-amber-200 border-amber-600/40",
    grad: "from-amber-500 to-yellow-600",
    text: "text-amber-300",
  },
  macros: {
    label: "Macros",
    host: "Roc 🏋️",
    icon: "💪",
    tagline: "Calories · protein · cut vs bulk scaling.",
    accent: "purple",
    ring: "ring-purple-500/60",
    chip: "bg-purple-950/60 text-purple-200 border-purple-600/40",
    grad: "from-purple-600 to-fuchsia-700",
    text: "text-purple-300",
  },
};
const POV_ORDER = ["recipe", "budget", "macros"];

function fmtUsd(n) {
  const v = Number(n) || 0;
  return `$${v.toFixed(2)}`;
}

export default function KitchenHubPage() {
  const [pov, setPov] = useState("recipe");   // David's recipe POV is now authored (Feb 2026)
  const [meals, setMeals] = useState([]);
  const [activeMealId, setActiveMealId] = useState(null);
  const [mealDetail, setMealDetail] = useState(null);
  const [eggs, setEggs] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Load meal list on POV change
  useEffect(() => {
    const load = async () => {
      setLoadingList(true);
      try {
        const r = await axios.get(`${API}/kitchen-hub/meals?pov=${pov}`);
        setMeals(r.data?.meals || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load meals.");
      } finally {
        setLoadingList(false);
      }
    };
    load();
  }, [pov]);

  // Chief Cook eggs (bottom section)
  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/kitchen-hub/chief-cook`);
        setEggs(r.data?.eggs || []);
      } catch (_) { /* silent */ }
    };
    load();
  }, []);

  // Meal detail loader
  useEffect(() => {
    if (!activeMealId) { setMealDetail(null); return; }
    const load = async () => {
      setLoadingDetail(true);
      try {
        const r = await axios.get(`${API}/kitchen-hub/meals/${activeMealId}?pov=${pov}`);
        setMealDetail(r.data || null);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load meal.");
      } finally {
        setLoadingDetail(false);
      }
    };
    load();
  }, [activeMealId, pov]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-black via-neutral-950 to-black text-white" data-testid="kitchen-hub-page">
      <Header />

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 space-y-8">
        {/* HERO */}
        <section className="relative overflow-hidden rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 via-black to-neutral-950 p-6" data-testid="kitchen-hub-hero">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500">The Kitchen · Informational Hub</div>
          <div className="mt-1 text-3xl sm:text-5xl font-black tracking-tight" style={{ fontFamily: "Anton, sans-serif" }}>
            SAME MEAL. <span className="text-amber-300">THREE LENSES.</span>
          </div>
          <div className="mt-2 text-sm text-neutral-400 max-w-2xl">
            The Kitchen is our real-world recipe hub — no combat, no crafting, no game currency. Pick a character&apos;s lens to see the same dish through their eyes: David&apos;s cooking steps, Cassandra &amp; Ledger&apos;s budget breakdown, or Roc&apos;s macros.
          </div>
        </section>

        {/* POV TOGGLE */}
        <section data-testid="pov-toggle">
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">Choose your lens</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {POV_ORDER.map((k) => {
              const m = POV_META[k];
              const active = pov === k;
              return (
                <button
                  key={k}
                  onClick={() => { setPov(k); setActiveMealId(null); }}
                  data-testid={`pov-btn-${k}`}
                  className={`text-left rounded-xl p-4 border transition ${
                    active
                      ? `bg-gradient-to-br ${m.grad} border-transparent ring-2 ${m.ring} shadow-lg`
                      : "bg-black/40 border-neutral-800 hover:border-neutral-600"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-3xl">{m.icon}</span>
                    <div>
                      <div className={`font-black text-lg ${active ? "text-white" : m.text}`} style={{ fontFamily: "Anton, sans-serif" }}>
                        {m.label.toUpperCase()}
                      </div>
                      <div className={`text-[10px] uppercase tracking-widest ${active ? "text-white/90" : "text-neutral-500"}`}>
                        {m.host}
                      </div>
                    </div>
                  </div>
                  <div className={`mt-2 text-xs ${active ? "text-white/90" : "text-neutral-400"}`}>
                    {m.tagline}
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* MEAL GRID or MEAL DETAIL */}
        {activeMealId ? (
          <MealDetailView
            meal={mealDetail}
            pov={pov}
            loading={loadingDetail}
            onBack={() => { setActiveMealId(null); setMealDetail(null); }}
            onSwapPov={(nextPov) => setPov(nextPov)}
          />
        ) : (
          <section data-testid="meal-grid-section">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
              {loadingList ? "Loading…" : `${meals.length} meal${meals.length === 1 ? "" : "s"} · viewing ${POV_META[pov].label} lens`}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="meal-grid">
              {meals.map((m) => (
                <MealCard key={m.id} meal={m} pov={pov} onOpen={() => setActiveMealId(m.id)} />
              ))}
              {!loadingList && meals.length === 0 && (
                <div className="col-span-full text-center py-12 text-neutral-500 italic border border-dashed border-neutral-800 rounded-xl">
                  No meals seeded yet. Owner drops incoming.
                </div>
              )}
            </div>
          </section>
        )}

        {/* CHIEF COOK STOREFRONT (bottom) */}
        <ChiefCookSection eggs={eggs} />

        {/* 📊 Unified Daily Log — same log seen in Gym, Tracker, Calendar, AI Coach */}
        <section data-testid="kitchen-daily-log">
          <DailyLogPanel title="Today's Log" />
        </section>
      </main>

      <Footer />
    </div>
  );
}

/* ─────────────────────────── Meal Card ─────────────────────────── */
function MealCard({ meal, pov, onOpen }) {
  const povStatus = meal.povs?.[pov] || "coming_soon";
  const m = POV_META[pov];
  const isAuthored = povStatus === "authored";
  return (
    <button
      onClick={onOpen}
      data-testid={`meal-card-${meal.id}`}
      className={`text-left rounded-xl p-4 border transition group ${
        isAuthored
          ? `border-neutral-700 hover:border-transparent hover:ring-2 ${m.ring} bg-neutral-950 hover:bg-black`
          : "border-neutral-800 bg-black/40 hover:border-neutral-700"
      }`}
    >
      <div className="text-4xl mb-2">{meal.hero_emoji || "🍽️"}</div>
      <div className="font-black text-white text-base" style={{ fontFamily: "Anton, sans-serif" }}>
        {meal.name}
      </div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-widest mt-1">{meal.category}</div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {POV_ORDER.map((k) => {
          const status = meal.povs?.[k];
          const km = POV_META[k];
          const authored = status === "authored";
          return (
            <span
              key={k}
              data-testid={`meal-${meal.id}-pov-${k}-${authored ? "authored" : "soon"}`}
              className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border ${
                authored ? km.chip : "bg-neutral-900 text-neutral-600 border-neutral-800"
              }`}
              title={`${km.host} · ${authored ? "authored" : "coming soon"}`}
            >
              {km.icon} {km.label}
            </span>
          );
        })}
      </div>
      {!isAuthored && (
        <div className="mt-3 text-[10px] text-neutral-500 italic">
          {m.icon} {m.label} lens — coming soon for this meal.
        </div>
      )}
    </button>
  );
}

/* ─────────────────────────── Meal Detail ─────────────────────────── */
function MealDetailView({ meal, pov, loading, onBack, onSwapPov }) {
  if (loading || !meal) {
    return (
      <div className="rounded-xl border border-neutral-800 p-8 text-center text-neutral-500 italic" data-testid="meal-detail-loading">
        Loading meal…
      </div>
    );
  }
  const m = POV_META[pov];
  const status = meal.pov_status?.[pov];
  const authored = status === "authored";

  return (
    <section className="space-y-4" data-testid="meal-detail">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          data-testid="meal-detail-back-btn"
          className="text-xs text-neutral-400 hover:text-white transition"
        >
          ← Back to meals
        </button>
        <div className="flex gap-1.5">
          {POV_ORDER.map((k) => {
            const km = POV_META[k];
            const active = pov === k;
            return (
              <button
                key={k}
                onClick={() => onSwapPov(k)}
                data-testid={`meal-detail-swap-${k}`}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border transition ${
                  active
                    ? `${km.chip} ring-1 ${km.ring}`
                    : "bg-neutral-900 text-neutral-400 border-neutral-800 hover:text-white hover:border-neutral-600"
                }`}
              >
                {km.icon} {km.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`rounded-2xl border border-neutral-800 bg-gradient-to-br from-neutral-950 to-black p-6`}>
        <div className="text-6xl mb-2">{meal.hero_emoji}</div>
        <div className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
          {meal.name}
        </div>
        <div className={`text-[10px] uppercase tracking-widest mt-2 ${m.text}`}>
          {m.icon} {m.host} · {m.label} lens
        </div>
      </div>

      {!authored ? (
        <div className="rounded-xl border border-dashed border-neutral-800 p-8 text-center" data-testid="meal-detail-coming-soon">
          <div className="text-5xl mb-2">{m.icon}</div>
          <div className="text-lg font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
            {m.label.toUpperCase()} POV — COMING SOON
          </div>
          <div className="text-xs text-neutral-500 italic mt-2 max-w-md mx-auto">
            {m.host} hasn&apos;t weighed in on this dish yet. Swap to a different lens above, or check back later.
          </div>
        </div>
      ) : pov === "budget" ? (
        <BudgetPOV data={meal.pov_data} />
      ) : pov === "recipe" ? (
        <RecipePOV data={meal.pov_data} />
      ) : (
        <div className="rounded-xl border border-neutral-800 p-6 text-neutral-400 text-sm" data-testid="meal-detail-generic">
          <PovImage image={meal.pov_data?.image} alt={`${m.host} · ${m.label}`} accent={m.accent} />
          <pre className="whitespace-pre-wrap break-words text-xs mt-4">
            {JSON.stringify(meal.pov_data, null, 2)}
          </pre>
        </div>
      )}
    </section>
  );
}

/* ─── Shared: Per-POV image slot (host's photo of their version) ─── */
function PovImage({ image, alt, accent }) {
  const ringColor = {
    amber: "border-amber-500/40 bg-amber-950/20",
    emerald: "border-emerald-500/40 bg-emerald-950/20",
    sky: "border-sky-500/40 bg-sky-950/20",
    blue: "border-blue-500/40 bg-blue-950/20",
    purple: "border-purple-500/40 bg-purple-950/20",
  }[accent] || "border-neutral-700 bg-neutral-900/40";
  if (image) {
    return (
      <div className={`rounded-xl overflow-hidden border ${ringColor}`} data-testid="pov-image">
        <img src={image} alt={alt || "POV recipe photo"} className="w-full h-auto object-cover" loading="lazy" />
      </div>
    );
  }
  return (
    <div
      className={`rounded-xl border border-dashed ${ringColor} p-6 text-center`}
      data-testid="pov-image-placeholder"
    >
      <div className="text-4xl opacity-40">📷</div>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-2">
        Photo Coming Soon
      </div>
      <div className="text-[11px] italic text-neutral-500 mt-1 max-w-xs mx-auto">
        {alt || "Host's photo of this dish"} will drop here once the plate is styled.
      </div>
    </div>
  );
}

/* ─── David's Recipe POV render (amber/orange lens) ─── */
function RecipePOV({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="recipe-pov">
      {/* Host intro */}
      <div className="rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-950/40 via-black to-black p-5">
        <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">
          🍳 David Lauer · The Chief Cook
        </div>
        {data.host_line && (
          <div className="text-[10px] text-amber-200/80 italic mb-2">{data.host_line}</div>
        )}
        <div className="text-sm text-neutral-200 whitespace-pre-line italic leading-relaxed">
          {data.host_intro}
        </div>
      </div>

      {/* Photo slot (per-POV image) */}
      <PovImage image={data.image} alt="David's meatloaf photo" accent="amber" />

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Servings"  value={data.servings || "—"} accent="amber" />
        <StatBox label="Prep Time" value={data.prep_time || "—"} accent="amber" />
        <StatBox label="Cook Time" value={data.cook_time || "—"} accent="amber" />
        <StatBox label="Oven"      value={data.oven_temp || "—"} accent="amber" />
      </div>

      {/* Ingredients */}
      {data.ingredients?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">🧂 Ingredients</div>
          <div className="rounded-xl border border-neutral-800 bg-black/50 p-4">
            <table className="w-full text-xs">
              <tbody>
                {data.ingredients.map((row, j) => (
                  <tr key={j} className="border-b border-neutral-900 last:border-0" data-testid={`recipe-ingredient-${j}`}>
                    <td className="py-1.5 pr-2 text-neutral-200">{row.item}</td>
                    <td className="py-1.5 text-amber-300 font-bold text-right whitespace-nowrap">{row.amount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Steps */}
      {data.steps?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">👨‍🍳 Step-by-Step</div>
          <div className="space-y-3">
            {data.steps.map((step, j) => (
              <div key={j} className="rounded-xl border border-neutral-800 bg-black/40 p-4" data-testid={`recipe-step-${j}`}>
                <div className="text-sm font-black text-amber-300" style={{ fontFamily: "Anton, sans-serif" }}>
                  {step.title}
                </div>
                <div className="mt-2 text-xs text-neutral-300 leading-relaxed whitespace-pre-line">
                  {step.body}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* David's signature line */}
      {data.david_signature && (
        <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-black p-4" data-testid="david-signature">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">🍳 David&apos;s Note</div>
          <div className="text-sm text-amber-100 italic leading-relaxed">
            {data.david_signature}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Cassandra & Ledger's Budget POV render ─── */
function BudgetPOV({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-6" data-testid="budget-pov">
      {/* Host intro */}
      <div className="rounded-xl border border-emerald-500/30 bg-gradient-to-br from-emerald-950/40 via-black to-black p-5">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1">
          🐦📓 Cassandra Gutkowski &amp; Ledger&apos;s Dramatic Money-Saving Meals
        </div>
        <div className="text-sm text-neutral-200 whitespace-pre-line italic leading-relaxed">
          {data.host_intro}
        </div>
      </div>

      {/* Photo slot (per-POV image) */}
      <PovImage image={data.image} alt="Cassandra & Ledger's plated version" accent="emerald" />

      {/* 🐦📓 Personalized insights from THIS user's actual receipts */}
      <CassandraInsightsPanel />

      {/* Stats strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Total Time"       value={data.total_prep_and_cook_time} />
        <StatBox label="Grand Prep"       value={data.grand_prep_time} />
        <StatBox label="Yield"            value={data.yield} />
        <StatBox label="Cost / Portion"   value={fmtUsd(data.cost_per_portion_usd)} accent="emerald" />
      </div>

      {/* Cost breakdown */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">💰 Cost Breakdown · Total {fmtUsd(data.total_cost_usd)}</div>
        <div className="space-y-3">
          {(data.cost_breakdown || []).map((act, i) => (
            <div key={i} className="rounded-xl border border-neutral-800 bg-black/50 p-4">
              <div className="text-sm font-black text-emerald-300 mb-2" style={{ fontFamily: "Anton, sans-serif" }}>
                {act.act.toUpperCase()}
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {(act.rows || []).map((row, j) => (
                    <tr key={j} className="border-b border-neutral-900 last:border-0">
                      <td className="py-1.5 pr-2 text-neutral-200">{row.ingredient}</td>
                      <td className="py-1.5 pr-2 text-neutral-500 hidden sm:table-cell">{row.amount}</td>
                      <td className="py-1.5 text-emerald-300 font-bold text-right whitespace-nowrap">{fmtUsd(row.cost_usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>

      {/* Ledger's Audit Tip */}
      {data.ledger_audit_tip && (
        <div className="rounded-xl border border-amber-500/40 bg-gradient-to-br from-amber-950/40 to-black p-4" data-testid="ledger-audit-tip">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 mb-1">🐦 Ledger&apos;s Audit Tip</div>
          <div className="text-sm text-amber-100 italic leading-relaxed">
            {data.ledger_audit_tip}
          </div>
        </div>
      )}

      {/* Acts */}
      <div>
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">🎬 The Steps · 3-Act Theatrical Structure</div>
        <div className="space-y-4">
          {(data.acts || []).map((act, i) => (
            <div key={i} className="rounded-xl border border-neutral-800 bg-black/40 p-4">
              <div className="text-lg font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
                🎭 {act.title}
              </div>
              <div className="mt-3 space-y-3">
                {(act.steps || []).map((step, j) => (
                  <div key={j} className="border-l-2 border-emerald-500/50 pl-3">
                    <div className="text-sm font-bold text-emerald-200">{step.title}</div>
                    <div className="mt-1 text-xs text-neutral-300 leading-relaxed whitespace-pre-line">{step.body}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  const grad = {
    emerald: "from-emerald-800/40 to-emerald-950/40 border-emerald-600/40 text-emerald-200",
    amber:   "from-amber-800/40 to-amber-950/40 border-amber-600/40 text-amber-200",
    sky:     "from-sky-800/40 to-sky-950/40 border-sky-600/40 text-sky-200",
    blue:    "from-blue-800/40 to-blue-950/40 border-blue-600/40 text-blue-200",
    purple:  "from-purple-800/40 to-purple-950/40 border-purple-600/40 text-purple-200",
  }[accent] || "from-neutral-900 to-neutral-950 border-neutral-800 text-neutral-200";
  return (
    <div className={`rounded-xl border bg-gradient-to-br ${grad} p-3`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-400">{label}</div>
      <div className="text-sm font-black mt-1 leading-tight">{value}</div>
    </div>
  );
}

/* ─────────────────────────── Chief Cook Storefront ─────────────────────────── */
function ChiefCookSection({ eggs }) {
  if (!eggs?.length) return null;
  return (
    <section className="pt-4 border-t border-neutral-800" data-testid="chief-cook-section">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500">Chief Cook · Character Support</div>
      <div className="mt-1 text-2xl font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
        SUPPORT THE HOSTS
      </div>
      <div className="text-xs text-neutral-400 mt-1 max-w-2xl">
        Each POV host has a matching tip egg. Buying an egg canonically routes to that character&apos;s tip flow.
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {eggs.map((e) => (
          <div
            key={e.id}
            data-testid={`chief-cook-egg-${e.id}`}
            className={`rounded-xl border p-4 ${
              e.status === "active"
                ? "border-amber-500/50 bg-gradient-to-br from-amber-950/40 to-black shadow-lg shadow-amber-900/30"
                : "border-neutral-800 bg-black/40"
            }`}
          >
            <div className="text-4xl mb-2">{e.icon}</div>
            <div className="text-sm font-black text-white" style={{ fontFamily: "Anton, sans-serif" }}>
              {e.host.toUpperCase()}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 mt-1">{e.role}</div>
            <div className="text-[11px] text-neutral-400 italic mt-2 leading-snug">{e.tagline}</div>
            <div className="mt-3 flex items-center justify-between">
              <div className={`text-xs font-black ${e.status === "active" ? "text-amber-300" : "text-neutral-600"}`}>
                {e.price_usd ? `$${e.price_usd.toFixed(2)}` : "TBD"}
              </div>
              <button
                disabled={e.status !== "active"}
                data-testid={`chief-cook-cta-${e.id}`}
                className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full transition ${
                  e.status === "active"
                    ? "bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-black"
                    : "bg-neutral-900 text-neutral-600 cursor-not-allowed border border-neutral-800"
                }`}
                onClick={() => {
                  if (e.status !== "active") return;
                  // David's tip flow lives at /chief-cook/david (existing ChefProfile route)
                  if (e.id === "egg_david") {
                    window.location.href = "/chief-cook/david";
                  }
                }}
              >
                {e.cta}
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
